import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Lock, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import tapLogo from "../assets/tap-logo.png";

type PageState = "loading" | "form" | "success" | "error";

export default function ResetPassword() {
  const [_, setLocation] = useLocation();
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [pageState, setPageState] = useState<PageState>("loading");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{
    hasLength: boolean;
    hasUpper: boolean;
    hasLower: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
  }>({
    hasLength: false,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
  });

  // Extract oobCode from URL and verify it
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');

    if (!code) {
      setPageState("error");
      setError("Invalid or missing reset link. Please request a new password reset.");
      return;
    }

    setOobCode(code);

    // Verify the password reset code
    verifyPasswordResetCode(auth, code)
      .then((userEmail) => {
        setEmail(userEmail);
        setPageState("form");
      })
      .catch((error: any) => {
        console.error("Error verifying reset code:", error);
        setPageState("error");
        
        if (error.code === "auth/expired-action-code") {
          setError("This password reset link has expired. Please request a new one.");
        } else if (error.code === "auth/invalid-action-code") {
          setError("This password reset link is invalid or has already been used.");
        } else {
          setError("Unable to verify password reset link. Please request a new one.");
        }
      });
  }, []);

  // Update password strength indicators
  useEffect(() => {
    setPasswordStrength({
      hasLength: newPassword.length >= 8,
      hasUpper: /[A-Z]/.test(newPassword),
      hasLower: /[a-z]/.test(newPassword),
      hasNumber: /[0-9]/.test(newPassword),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    });
  }, [newPassword]);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/[0-9]/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return "Password must contain at least one special character";
    }
    return null;
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!oobCode) {
      setError("Invalid reset code. Please request a new password reset.");
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setPageState("success");
      
      // Redirect to landing page after 3 seconds
      setTimeout(() => {
        setLocation("/");
      }, 3000);
    } catch (error: any) {
      console.error("Error resetting password:", error);
      
      if (error.code === "auth/weak-password") {
        setError("Password is too weak. Please choose a stronger password.");
      } else if (error.code === "auth/expired-action-code") {
        setError("This password reset link has expired. Please request a new one.");
      } else if (error.code === "auth/invalid-action-code") {
        setError("This password reset link is invalid or has already been used.");
      } else {
        setError("Failed to reset password. Please try again or request a new reset link.");
      }
    } finally {
      setLoading(false);
    }
  };

  const allRequirementsMet = Object.values(passwordStrength).every(Boolean);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#594FE0] from-[1.7%] via-[#4175E6] via-[45.57%] to-[#34C7FA] to-[96.2%] flex items-center justify-center p-3 sm:p-4 md:p-6">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center space-y-3 sm:space-y-4 pb-3 sm:pb-4 px-4 sm:px-6">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-1 sm:mb-2">
            <img 
              src={tapLogo} 
              alt="T.A.P. Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <CardTitle className="text-xl sm:text-2xl font-bold text-slate-800 space-grotesk">
            {pageState === "loading" && "Verifying Link..."}
            {pageState === "form" && "Reset Your Password"}
            {pageState === "success" && "Password Reset Successful"}
            {pageState === "error" && "Reset Link Invalid"}
          </CardTitle>
          <CardDescription className="text-sm sm:text-base text-slate-600 space-grotesk px-2 sm:px-0">
            {pageState === "loading" && "Please wait while we verify your reset link"}
            {pageState === "form" && `Enter a new password for ${email}`}
            {pageState === "success" && "You can now sign in with your new password"}
            {pageState === "error" && "There was a problem with your reset link"}
          </CardDescription>
        </CardHeader>

        <CardContent className="px-4 sm:px-6">
          {pageState === "loading" && (
            <div className="flex flex-col items-center justify-center py-6 sm:py-8">
              <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 animate-spin text-orange-500 mb-3 sm:mb-4" />
              <p className="text-xs sm:text-sm text-slate-600 space-grotesk text-center px-4">Verifying your reset link...</p>
            </div>
          )}

          {pageState === "error" && (
            <div className="space-y-4 sm:space-y-6">
              <Alert variant="destructive" className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <AlertDescription className="space-grotesk text-xs sm:text-sm">
                  {error}
                </AlertDescription>
              </Alert>
              <Button
                variant="primary"
                size="primary"
                className="w-full h-11 sm:h-12 text-sm sm:text-base"
                onClick={() => setLocation("/")}
              >
                Return to Home
              </Button>
            </div>
          )}

          {pageState === "success" && (
            <div className="space-y-4 sm:space-y-6">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                <AlertDescription className="text-green-800 space-grotesk text-xs sm:text-sm">
                  <strong className="font-semibold">Success!</strong>
                  <br />
                  Your password has been reset. Redirecting to login...
                </AlertDescription>
              </Alert>
              <Button
                variant="primary"
                size="primary"
                className="w-full h-11 sm:h-12 text-sm sm:text-base"
                onClick={() => setLocation("/")}
              >
                Go to Sign In
              </Button>
            </div>
          )}

          {pageState === "form" && (
            <form onSubmit={handleResetPassword} className="space-y-4 sm:space-y-5">
              {error && (
                <Alert variant="destructive" className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <AlertDescription className="space-grotesk text-xs sm:text-sm">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="new-password" className="text-slate-700 font-medium space-grotesk text-sm">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                    className="h-11 sm:h-12 border-slate-200 focus:border-orange-400 focus:ring-orange-400/20 focus:ring-2 pr-10 rounded-lg space-grotesk text-sm sm:text-base"
                    placeholder="Enter your new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {newPassword && (
                <div className="bg-slate-50 rounded-lg p-3 sm:p-4 space-y-2">
                  <p className="text-xs font-semibold text-slate-700 space-grotesk mb-1.5 sm:mb-2">
                    Password Requirements:
                  </p>
                  <div className="space-y-1 sm:space-y-1.5 text-xs">
                    <div className={`flex items-center gap-1.5 sm:gap-2 ${passwordStrength.hasLength ? 'text-green-600' : 'text-slate-500'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${passwordStrength.hasLength ? 'bg-green-600' : 'bg-slate-300'}`} />
                      <span className="space-grotesk">At least 8 characters</span>
                    </div>
                    <div className={`flex items-center gap-1.5 sm:gap-2 ${passwordStrength.hasUpper ? 'text-green-600' : 'text-slate-500'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${passwordStrength.hasUpper ? 'bg-green-600' : 'bg-slate-300'}`} />
                      <span className="space-grotesk">One uppercase letter</span>
                    </div>
                    <div className={`flex items-center gap-1.5 sm:gap-2 ${passwordStrength.hasLower ? 'text-green-600' : 'text-slate-500'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${passwordStrength.hasLower ? 'bg-green-600' : 'bg-slate-300'}`} />
                      <span className="space-grotesk">One lowercase letter</span>
                    </div>
                    <div className={`flex items-center gap-1.5 sm:gap-2 ${passwordStrength.hasNumber ? 'text-green-600' : 'text-slate-500'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${passwordStrength.hasNumber ? 'bg-green-600' : 'bg-slate-300'}`} />
                      <span className="space-grotesk">One number</span>
                    </div>
                    <div className={`flex items-center gap-1.5 sm:gap-2 ${passwordStrength.hasSpecial ? 'text-green-600' : 'text-slate-500'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${passwordStrength.hasSpecial ? 'bg-green-600' : 'bg-slate-300'}`} />
                      <span className="space-grotesk">One special character</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="confirm-password" className="text-slate-700 font-medium space-grotesk text-sm">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                    className="h-11 sm:h-12 border-slate-200 focus:border-orange-400 focus:ring-orange-400/20 focus:ring-2 pr-10 rounded-lg space-grotesk text-sm sm:text-base"
                    placeholder="Confirm your new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                variant="primary"
                size="primary"
                className="w-full h-11 sm:h-12 text-sm sm:text-base"
                disabled={loading || !allRequirementsMet}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <span className="hidden xs:inline">Resetting Password...</span>
                    <span className="xs:hidden">Resetting...</span>
                  </>
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    Reset Password
                  </>
                )}
              </Button>

              <div className="text-center pt-1">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setLocation("/")}
                  className="font-medium space-grotesk text-xs sm:text-sm"
                >
                  Back to Sign In
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

